// API Response Types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  timestamp: string;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

// Auth Types
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  expiresIn: number;
  type?: string;
  user: User;
}

export type ContextType = 'PLATFORM' | 'GROUP' | 'RESTAURANT';

/**
 * One row in {@link User.roles} -- a scoped role assignment.
 *
 * Mirrors backend `UserRoleSummary`. The same role can appear multiple times
 * with different contexts (e.g. RESTAURANT_VIEWER scoped to two different
 * restaurants returns two entries with the same `name` but different
 * `restaurantId`).
 */
export interface UserRoleAssignment {
  // Role identity
  id: number;
  uuid?: string;
  name: string;
  displayName?: string;
  description?: string;

  // Assignment context
  assignmentId?: number;
  contextType?: ContextType;
  restaurantGroupId?: number | null;
  restaurantGroupName?: string | null;
  restaurantId?: number | null;
  restaurantName?: string | null;
}

export interface User {
  id: number;
  uuid?: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  timezone?: string;
  locale?: string;
  theme?: string;
  roles?: UserRoleAssignment[];
  permissions?: Permission[];
  active: boolean;
  locked?: boolean;
  emailVerified?: boolean;
  twoFactorEnabled?: boolean;
  createdDate?: string;
  modifiedDate?: string;
  lastLoginDate?: string;
}

export interface Role {
  id: number;
  uuid?: string;
  name: string;
  displayName?: string;
  description: string;
  roleLevel?: ContextType;
  roleType?: 'SYSTEM' | 'PREDEFINED' | 'CUSTOM';
  systemRole?: boolean;
  active?: boolean;
  permissions: Permission[];
  createdDate?: string;
  modifiedDate?: string;
}

/**
 * One scoped role assignment in a user create/update request.
 * Cross-field rules enforced by the backend; the modal also enforces
 * them client-side to avoid round-trips.
 */
export interface RoleAssignment {
  roleId: number;
  contextType: ContextType;
  restaurantGroupId?: number | null;
  restaurantId?: number | null;
}

export interface UserCreateRequest {
  username: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  timezone?: string;
  locale?: string;
  active?: boolean;
  assignments?: RoleAssignment[];
}

export interface UserUpdateRequest {
  email: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  timezone?: string;
  locale?: string;
  active?: boolean;
  assignments?: RoleAssignment[];
}

/** Mirrors backend RoleCreateRequest. roleLevel/roleType default server-side
 *  (RESTAURANT/CUSTOM) when omitted -- see RolesPage report 11's H1. */
export interface RoleCreateRequest {
  name: string;
  displayName?: string;
  description?: string;
  roleLevel?: ContextType;
  roleType?: 'SYSTEM' | 'CUSTOM';
  active?: boolean;
  permissionIds?: number[];
}

/** Mirrors backend RoleUpdateRequest. All fields optional; only non-null
 *  values are applied. No roleLevel/roleType -- immutable after creation. */
export interface RoleUpdateRequest {
  name?: string;
  displayName?: string;
  description?: string;
  active?: boolean;
  permissionIds?: number[];
}

export interface Permission {
  id: number;
  uuid?: string;
  name: string;
  displayName?: string;
  resource: string;
  action: string;
  category?: string;
  description: string;
  systemPermission?: boolean;
  active?: boolean;
}

// Restaurant Types
export interface Restaurant {
  id: number;
  uuid: string;
  groupId: number;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  email: string;
  website?: string;
  timezone: string;
  currency: string;
  currencyName?: string;
  currencySymbol?: string;
  extendedDayHours?: number;
  maxTerminals?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// Table Types
export interface TableInfo {
  id: number;
  restaurantId: number;
  posTableId?: number;
  tableNumber: string;
  section: string;
  capacity: number;
  status: TableStatus;
  currentTicketId?: string;
  covers?: number;
  serverName?: string;
  timeSeated?: string;
  statusName?: string;
  statusColorRgb?: number;
  updatedAt: string;
}

export interface TableStatusOption {
  posStatusId: string;
  name: string;
  priority: number;
  colorRgb?: number;
  type?: string;
}

// Full TableStatus definition (management CRUD). type ∈ MEAL | TABLE | BOOKING.
export interface TableStatusDef {
  posStatusId: string;
  uuid: string;
  name: string;
  type?: string;
  subtype1?: string;
  subtype2?: string;
  priority?: number;
  buttonColorRgb?: number;
  textColorRgb?: number;
  enabled?: boolean;
}

export interface TableStatusDefInput {
  name: string;
  type?: string;
  subtype1?: string;
  subtype2?: string;
  priority?: number;
  buttonColorRgb?: number;
  textColorRgb?: number;
  enabled?: boolean;
}

export enum TableStatus {
  AVAILABLE = 'AVAILABLE',
  OCCUPIED = 'OCCUPIED',
  RESERVED = 'RESERVED',
  CLEANING = 'CLEANING',
  OUT_OF_SERVICE = 'OUT_OF_SERVICE',
}

// POS Terminal Types
export interface PosTerminal {
  id: number;
  uuid: string;
  restaurantId: number;
  terminalId: string;
  name: string;
  ipAddress: string;
  macAddress: string;
  status: TerminalStatus;
  lastHeartbeat?: string;
  appVersion: string;
  deviceModel: string;
  osVersion: string;
  location: string;
  active: boolean;
  registrationDate?: string;
  createdDate: string;
  modifiedDate: string;
}

export enum TerminalStatus {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
  MAINTENANCE = 'MAINTENANCE',
  ERROR = 'ERROR',
}

// Menu Types
export interface MenuItem {
  id: number;
  restaurantId: number;
  name: string;
  description?: string;
  category: string;
  menuGroup: string;
  groupId?: number;
  categoryId?: number;
  price: number;
  cost?: number;
  available: boolean;
  imageUrl?: string;
  allergens?: string[];
  nutritionalInfo?: Record<string, any>;
  preparationTime?: number;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Extended fields shared by MenuItemApiResponse and UpdateMenuItemPayload —
// mirrors the fields MenuManagementServiceImpl actually persists/returns
// beyond the base name/description/price/available/sortOrder set.
interface MenuItemExtendedFields {
  unitName?: string;
  barcode?: string;
  buyPrice?: number;
  stockAmount?: number;
  manageStock?: boolean;
  disableWhenStockAmountIsZero?: boolean;
  webPrice?: number;
  discountRate?: number;
  minQuantityGroup?: number;
  maxQuantityGroup?: number;
  enforceInstructions?: boolean;
  appendToParentName?: boolean;
  prependToParentName?: boolean;
  buttonColorCode?: number;
  textColorCode?: number;
  visibleOnSite?: boolean;
  hideOnKitchen?: boolean;
  showStatusButton?: boolean;
  hideOnReceipt?: boolean;
  askMealDeal?: boolean;
  askForPrice?: boolean;
  giveRestaurantPoints?: boolean;
  alcoholicDrink?: boolean;
  hasDouble?: boolean;
  freeDisplayForChild?: boolean;
  prependAsterisk?: boolean;
  disableCookingInfoOnline?: boolean;
  openItem?: boolean;
  saleAreaId?: string;
  taxId?: number;
  printerGroupId?: number;
  orderTypeIds?: number[];
  cookingInstructionIds?: number[];
  additionalPrinterGroupIds?: number[];
  shiftIds?: number[];
  bannedTableIds?: number[];
  translations?: TranslationEntry[];
  modifierGroups?: ModifierGroupData[];
}

// Raw shape returned by GET /v1/restaurants/{id}/menu/items and
// GET /v1/restaurants/{id}/menu/groups/{groupId}/items (backend MenuItemResponse DTO).
// Note the visibility flag is named `visible` here, not `available` as in MenuItem.
export interface MenuItemApiResponse extends MenuItemExtendedFields {
  id: number;
  uuid?: string;
  groupId?: number;
  groupName?: string;
  categoryId?: number;
  categoryName?: string;
  name: string;
  description?: string;
  price: number;
  visible?: boolean;
  sortOrder?: number;
  variants?: Record<string, any>;
  allergens?: string[];
  dietaryFlags?: Record<string, boolean>;
  imageUrl?: string;
  posMenuItemId?: number;
  createdDate?: string;
  modifiedDate?: string;
}

export interface UpdateMenuItemPayload extends MenuItemExtendedFields {
  name?: string;
  description?: string;
  price?: number;
  available?: boolean;
  sortOrder?: number;
  groupId?: number;
  variants?: Record<string, any>;
  allergens?: string[];
  dietaryFlags?: Record<string, boolean>;
  imageUrl?: string;
}

export interface ManagedMenuCategory {
  id: number;
  restaurantId: number;
  name: string;
  description?: string;
  imageUrl?: string;
  sortOrder: number;
  active: boolean;
  posMenuCategoryId?: number;
  buttonColorCode?: number;
  textColorCode?: number;
  beverage: boolean;
  menuGroupShortcut: boolean;
  shiftIds?: number[];
  groupCount: number;
  syncStatus: ManagedSyncStatus;
}

export interface ManagedMenuGroup {
  id: number;
  categoryId: number;
  categoryName: string;
  name: string;
  description?: string;
  sortOrder: number;
  visible: boolean;
  posGroupId?: number;
  buttonColorCode?: number;
  textColorCode?: number;
  imageUrl?: string;
  visibleOnSite: boolean;
  enforceInstructions: boolean;
  onlyManager: boolean;
  staffMeal: boolean;
  showNotification: boolean;
  notification?: string;
  shiftIds?: number[];
  itemCount: number;
  syncStatus: ManagedSyncStatus;
}

export interface MenuImportResult {
  categoriesCreated: number;
  groupsCreated: number;
  itemsCreated: number;
  skipped: number;
}

export interface PosImportResult {
  totalCreated: number;
  totalSkipped: number;
  entities: Record<string, { created: number; skipped: number }>;
}

// WebSocket Message Types
export interface WebSocketMessage<T = any> {
  type: string;
  payload: T;
  timestamp: string;
}

export interface TableUpdateMessage {
  restaurantId: string;
  tableId: number;
  status: TableStatus;
  ticketId?: string;
  covers?: number;
  tableData?: Record<string, any>;
  timestamp: string;
  messageId: string;
}

export interface MenuUpdateMessage {
  restaurantId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  entityType: string;
  entityId: number;
  entityWebId: string;
  entityData?: Record<string, any>;
  syncVersion: number;
  timestamp: string;
}

export interface PosHeartbeatMessage {
  terminalId: string;
  restaurantUuid: string;
  timestamp: string;
  status: string;
  metadata?: Record<string, any>;
}

// Session payload returned by GET /api/v1/me
export interface UserSummary {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface MeResponse {
  user: UserSummary;
  permissions: string[];   // sorted ascending; canonical authority strings (e.g. "restaurant.read")
  scope: DashboardScopeInfo;
}

// Dashboard Stats
export interface DashboardScopeInfo {
  type: 'PLATFORM' | 'GROUP' | 'RESTAURANT' | 'MIXED' | 'EMPTY';
  groupNames: string[];
  restaurantNames: string[];
  restaurantCount: number;
}

export interface DashboardStats {
  totalRestaurants: number | null;
  activeRestaurants: number;
  totalTerminals: number | null;
  onlineTerminals: number | null;
  totalTables: number;
  occupiedTables: number;
  todayRevenue: number;
  revenueChange: number;
  demoMode?: boolean;
  currency: string;
  locale: string;
  scope?: DashboardScopeInfo;
  tableStatusDistribution?: TableStatusEntry[];
  revenueByHour?: RevenueHourEntry[];
  topMenuItems?: TopMenuItemEntry[];
  recentActivity?: ActivityEntry[];
}

export interface TableStatusEntry {
  name: string;
  value: number;
  color: string;
}

export interface RevenueHourEntry {
  time: string;
  revenue: number;
  orders: number;
}

export interface TopMenuItemEntry {
  name: string;
  orders: number;
  revenue: number;
}

export interface ActivityEntry {
  id: string;
  icon: string;
  text: string;
  time: string;
  color: string;
}

// Restaurant Group Types
export interface RestaurantGroup {
  id: number;
  uuid: string;
  name: string;
  description?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  // Real, persisted entity columns -- unreachable from the dashboard until
  // the group edit form grew Address/Billing sections (report 12's H2).
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  billingAddressLine1?: string;
  billingAddressLine2?: string;
  billingCity?: string;
  billingState?: string;
  billingZipCode?: string;
  billingCountry?: string;
  // Computed by the controller from RestaurantService.countByGroupId -- not a persisted column.
  restaurantCount?: number;
}

/** Mirrors backend RestaurantGroupCreateRequest. Only `name` is required. */
export interface RestaurantGroupCreateRequest {
  name: string;
  legalName?: string;
  description?: string;
  subscriptionTier?: 'BASIC' | 'PROFESSIONAL' | 'PREMIUM' | 'ENTERPRISE';
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  trialEndsDate?: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  billingEmail?: string;
  billingAddressLine1?: string;
  billingAddressLine2?: string;
  billingCity?: string;
  billingState?: string;
  billingZipCode?: string;
  billingCountry?: string;
  maxRestaurants?: number;
  maxUsersPerRestaurant?: number;
  maxPosTerminalsPerRestaurant?: number;
}

/** Mirrors backend RestaurantGroupUpdateRequest. All fields optional; no
 *  `name` -- immutable after creation. */
export interface RestaurantGroupUpdateRequest {
  legalName?: string;
  description?: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  billingEmail?: string;
  billingAddressLine1?: string;
  billingAddressLine2?: string;
  billingCity?: string;
  billingState?: string;
  billingZipCode?: string;
  billingCountry?: string;
  active?: boolean;
}

export interface RestaurantInGroup extends Restaurant {
  groupId: number;
}

/** Mirrors backend SyncStatusResponse (GET /v1/restaurants/{id}/sync/status). */
export interface RestaurantSyncStatus {
  pendingCount: number;
  processingCount: number;
  sentCount: number;
  failedCount: number;
  conflictCount: number;
  syncing: boolean;
}

// Sync Types — aligned with backend SyncOutboxResponse / SyncConflictResponse / enums.
// SyncOutboxStatus enum (server-core): PENDING, PROCESSING, SENT, ACKNOWLEDGED, FAILED, CANCELLED
export type SyncMessageStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'SENT'
  | 'ACKNOWLEDGED'
  | 'FAILED'
  | 'CANCELLED';

// InboxStatus enum (server-core): RECEIVED, PROCESSING, APPLIED, CONFLICTED, FAILED
export type SyncInboxStatus =
  | 'RECEIVED'
  | 'PROCESSING'
  | 'APPLIED'
  | 'CONFLICTED'
  | 'FAILED';

export type SyncAction = 'CREATE' | 'UPDATE' | 'DELETE';

// Mirrors com.time2dine.management.api.dto.sync.SyncOutboxResponse
export interface SyncOutboxMessage {
  id: number;
  syncId: string; // UUID
  restaurantId: number;
  action: SyncAction;
  entityType: string;
  entityId: number;
  payload?: Record<string, any>;
  status: SyncMessageStatus;
  retryCount: number;
  maxRetries: number;
  scheduledAt?: string;
  processedAt?: string;
  acknowledgedAt?: string;
  errorMessage?: string;
  posEntityId?: number;
}

// Mirrors the inbox map produced by SyncMonitoringController.mapInboxToResponse / SyncDlqController.mapInboxToResponse
export interface SyncInboxMessage {
  id: number;
  messageId: string; // UUID
  restaurantId: number;
  terminalId?: string;
  action: SyncAction;
  entityType: string;
  entityId: number;
  posEntityId?: number;
  payload?: Record<string, any>;
  entityVersion?: number;
  status: SyncInboxStatus;
  processedAt?: string;
  errorMessage?: string;
  createdDate: string;
  modifiedDate?: string;
}

// ConflictStatus enum (server-core): PENDING, RESOLVED, AUTO_RESOLVED, FAILED
export type ConflictStatus = 'PENDING' | 'RESOLVED' | 'AUTO_RESOLVED' | 'FAILED';

// ConflictResolution enum (server-core): KEEP_MANAGEMENT, KEEP_POS, MERGE, IGNORE
export type ConflictResolution = 'KEEP_MANAGEMENT' | 'KEEP_POS' | 'MERGE' | 'IGNORE';

// Mirrors com.time2dine.management.api.dto.sync.SyncConflictResponse
export interface SyncConflict {
  id: number;
  uuid: string;
  restaurantId: number;
  entityType: string;
  entityId: number;
  entityName?: string;
  posEntityId?: number;
  managementVersion: Record<string, any>;
  posVersion: Record<string, any>;
  conflictingFields?: Record<string, any>;
  status: ConflictStatus;
  resolution?: ConflictResolution;
  resolvedVersion?: Record<string, any>;
  resolvedByUsername?: string;
  resolvedAt?: string;
  resolutionNotes?: string;
  managementModifiedAt?: string;
  posModifiedAt?: string;
  detectedAt: string;
}

// Mirrors com.time2dine.management.api.dto.sync.ResolveConflictRequest
export interface ResolveConflictRequest {
  resolution: ConflictResolution;
  mergedValues?: Record<string, any>;
  notes?: string;
}

// AI Menu Extraction Types
export interface AIExtractionJob {
  id: string;
  restaurantId: number;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress: number;
  totalPages: number;
  processedPages: number;
  extractedItems: ExtractedMenuItem[];
  errors?: string[];
  createdAt: string;
  completedAt?: string;
}

export interface ExtractedMenuItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  category: string;
  confidence: number;
  isNew: boolean;
  matchedExistingId?: number;
  changes?: {
    field: string;
    oldValue: string | number;
    newValue: string | number;
  }[];
  sourcePage: number;
  sourceRegion?: { x: number; y: number; width: number; height: number };
  selected: boolean;
}

export interface AIImportOptions {
  mode: 'ADD_ONLY' | 'SMART_MERGE' | 'REPLACE_CATEGORY';
  syncImmediately: boolean;
  itemsToKeep: number[];
  itemsToDelete: number[];
}

export interface AIImportResult {
  itemsAdded: number;
  itemsUpdated: number;
  itemsDeleted: number;
  syncJobId?: string;
}

// Billing Types
// Mirrors the real, flat backend shapes (BillingController's SubscriptionResponse /
// SubscriptionPlanResponse / UsageResponse) rather than a nested/derived shape the
// backend never actually sent (see frontend_analisis reports 15/16).
export type SubscriptionTier = 'BASIC' | 'PROFESSIONAL' | 'PREMIUM' | 'ENTERPRISE';

export interface SubscriptionPlan {
  id: number;
  name: string;
  description?: string;
  /** Pence, not pounds. */
  pricePerRestaurantPence: number;
  /** Arbitrary, admin-defined flag names — not a fixed known set. */
  features: Record<string, boolean>;
  maxRestaurants?: number;
  active: boolean;
  tier: SubscriptionTier;
}

/** Alias kept for the `/billing/plans` list endpoint, which returns the same shape. */
export type Plan = SubscriptionPlan;

export interface Subscription {
  id: number;
  plan: SubscriptionPlan;
  status: 'ACTIVE' | 'GRACE_PERIOD' | 'LIMITED' | 'SUSPENDED' | 'CANCELLED';
  restaurantCount: number;
  /** Pence, not pounds. */
  monthlyAmountPence: number;
  billingDay: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  gracePeriodEndsAt?: string;
}

export interface BillingUsage {
  restaurantCount: number;
  /** Hardcoded to 0 server-side for now (BillingController.getUsage). */
  terminalCount: number;
  /** Hardcoded to 0 server-side for now (BillingController.getUsage). */
  orderCount: number;
  periodStart: string;
  periodEnd: string;
}

export interface Invoice {
  id: number;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  amount: number;
  taxAmount: number;
  totalAmount: number;
  currency: string;
  status: 'DRAFT' | 'PENDING' | 'PAID' | 'OVERDUE' | 'VOID';
  pdfUrl?: string;
  lineItems: InvoiceLineItem[];
}

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export interface PaymentMethod {
  id: string;
  type: 'CARD' | 'DIRECT_DEBIT';
  isDefault: boolean;
  card?: {
    brand: string;
    last4: string;
    expiryMonth: number;
    expiryYear: number;
  };
  directDebit?: {
    bankName: string;
    last4: string;
    mandateStatus: 'ACTIVE' | 'PENDING' | 'CANCELLED';
  };
}

export interface BillingDetails {
  companyName: string;
  vatNumber?: string;
  address: {
    line1: string;
    line2?: string;
    city: string;
    postcode: string;
    country: string;
  };
  invoiceEmail: string;
}

// Sync Status (for managed entities)
export enum ManagedSyncStatus {
  NOT_SYNCED = 'NOT_SYNCED',
  SYNCED = 'SYNCED',
  PENDING_SYNC = 'PENDING_SYNC',
  CONFLICT = 'CONFLICT',
}

// Floor Management Types
export interface ShiftInfo {
  id: number;
  uuid: string;
  restaurantId: number;
  posShiftId?: number;
  name: string;
  startTime: string; // HH:mm
  endTime: string;
  shiftLength?: number;
  daysOfWeek?: string;
  startDate?: string; // ISO date
  endDate?: string;
  syncStatus: ManagedSyncStatus;
  createdDate: string;
  modifiedDate: string;
}

export interface FloorInfo {
  id: number;
  uuid: string;
  restaurantId: number;
  posFloorId?: number;
  name: string;
  sortOrder: number;
  width: number;
  height: number;
  hidden: boolean;
  foregroundColorCode?: number;
  backgroundColorCode?: number;
  syncStatus: ManagedSyncStatus;
  tableCount: number;
  zoneCount: number;
  layoutCount: number;
  createdDate: string;
  modifiedDate: string;
  backgroundImageBase64?: string;
  backgroundImageContentType?: string;
}

export interface TableZoneInfo {
  id: number;
  uuid: string;
  floorId: number;
  floorName: string;
  posZoneId?: number;
  name: string;
  description?: string;
  color?: string;
  enabled: boolean;
  sortOrder: number;
  syncStatus: ManagedSyncStatus;
  tableCount: number;
  createdDate: string;
  modifiedDate: string;
}

export interface ManagedTableInfo {
  id: number;
  uuid: string;
  floorId: number;
  floorName: string;
  zoneId?: number;
  zoneName?: string;
  posTableId?: number;
  name?: string;
  description?: string;
  capacity: number;
  shape: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize?: number;
  sortOrder: number;
  active: boolean;
  shiftIds: number[];
  syncStatus: ManagedSyncStatus;
  createdDate: string;
  modifiedDate: string;
}

export interface LayoutPositionInfo {
  tableId: number;
  tableName: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  fontSize?: number;
  hidden: boolean;
}

export interface FloorLayoutInfo {
  id: number;
  uuid: string;
  floorId: number;
  shiftId?: number;
  shiftName?: string;
  posLayoutId?: number;
  name: string;
  designWidth: number;
  designHeight: number;
  isDefault: boolean;
  syncStatus: ManagedSyncStatus;
  positions: LayoutPositionInfo[];
  createdDate: string;
  modifiedDate: string;
}

// Booking Configuration Types
export interface BookingRuleInfo {
  id: number;
  uuid: string;
  restaurantId: number;
  posRuleId?: number;
  name: string;
  type: string;
  parameterJson: Record<string, any>;
  enabled: boolean;
  description?: string;
  zoneIds: number[];
  sectionIds: number[];
  syncStatus: ManagedSyncStatus;
  createdDate: string;
  modifiedDate: string;
}

export interface BookingSectionInfo {
  id: number;
  uuid: string;
  restaurantId: number;
  posSectionId?: number;
  name: string;
  defaultDurationMinutes: number;
  sortOrder: number;
  enabled: boolean;
  color?: string;
  maxGuests?: number;
  maxBookingsPerSlot?: number;
  lastBookingMinutesBeforeClose?: number;
  syncStatus: ManagedSyncStatus;
  createdDate: string;
  modifiedDate: string;
}

export interface BookingScheduleInfo {
  id: number;
  uuid: string;
  restaurantId: number;
  posScheduleId?: number;
  dayOfWeek: number;
  openTime: string; // HH:mm
  closeTime: string;
  enabled: boolean;
  sectionIds: number[];
  syncStatus: ManagedSyncStatus;
  createdDate: string;
  modifiedDate: string;
}

export interface SpecialDayInfo {
  id: number;
  uuid: string;
  restaurantId: number;
  posSpecialDayId?: number;
  date: string; // ISO date
  description?: string;
  isClosed: boolean;
  openTime?: string;
  closeTime?: string;
  maxBookings?: number;
  notes?: string;
  floorLayoutId?: number;
  syncStatus: ManagedSyncStatus;
  createdDate: string;
  modifiedDate: string;
}

// Discount Options Types (for relationship selectors)
export interface PosEntityOption {
  posId: number;
  name: string;
}

export interface MenuGroupOption extends PosEntityOption {
  menuItemPosIds: number[];
}

export interface MenuCategoryOption extends PosEntityOption {
  menuItemPosIds: number[];
}

export interface DiscountOptions {
  orderTypes: PosEntityOption[];
  shifts: PosEntityOption[];
  menuItems: PosEntityOption[];
  menuGroups: MenuGroupOption[];
  menuCategories: MenuCategoryOption[];
}

// Error Types
export interface ApiError {
  message: string;
  code: string;
  details?: Record<string, any>;
  timestamp: string;
}

// Audit Log Types
export enum AuditAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILED = 'LOGIN_FAILED',
  LOGOUT = 'LOGOUT',
  PASSWORD_CHANGE = 'PASSWORD_CHANGE',
  PASSWORD_RESET_REQUEST = 'PASSWORD_RESET_REQUEST',
  ACCOUNT_LOCKED = 'ACCOUNT_LOCKED',
  ACCOUNT_UNLOCKED = 'ACCOUNT_UNLOCKED',
  PERMISSION_GRANTED = 'PERMISSION_GRANTED',
  PERMISSION_REVOKED = 'PERMISSION_REVOKED',
  ROLE_ASSIGNED = 'ROLE_ASSIGNED',
  ROLE_REMOVED = 'ROLE_REMOVED',
  CONFIG_CHANGED = 'CONFIG_CHANGED',
  SYNC_CONFLICT_RESOLVED = 'SYNC_CONFLICT_RESOLVED',
  DLQ_MESSAGE_REPLAYED = 'DLQ_MESSAGE_REPLAYED',
  DLQ_MESSAGE_DISCARDED = 'DLQ_MESSAGE_DISCARDED',
  SUBSCRIPTION_CHANGED = 'SUBSCRIPTION_CHANGED',
  PAYMENT_PROCESSED = 'PAYMENT_PROCESSED',
  EXPORT_DATA = 'EXPORT_DATA',
  IMPERSONATION_START = 'IMPERSONATION_START',
  IMPERSONATION_END = 'IMPERSONATION_END',
  TERMINAL_REGISTERED = 'TERMINAL_REGISTERED',
  TERMINAL_DEREGISTERED = 'TERMINAL_DEREGISTERED',
  BULK_OPERATION = 'BULK_OPERATION',
}

export enum AuditCategory {
  AUTH = 'AUTH',
  DATA = 'DATA',
  ACCESS = 'ACCESS',
  SYSTEM = 'SYSTEM',
  SENSITIVE = 'SENSITIVE',
}

export interface AuditLogEntry {
  eventId: string;
  timestamp: string;
  userId?: number;
  userEmail?: string;
  userRole?: string;
  impersonatorId?: number;
  action: AuditAction;
  category: AuditCategory;
  entityType?: string;
  entityId?: string;
  entityName?: string;
  tenantId?: string;
  restaurantId?: number;
  terminalId?: string;
  ipAddress?: string;
  beforeValue?: Record<string, any>;
  afterValue?: Record<string, any>;
  metadata?: Record<string, any>;
  recordHash: string;
}

export interface AuditVerificationResult {
  valid: boolean;
  brokenAt?: string;
  message: string;
}

// POS Terminal Errors
export type PosErrorLevel = 'ERROR' | 'FATAL';

export interface TerminalErrorEntry {
  id: number;
  clientUuid: string;
  occurredAt: string;
  receivedAt: string;
  terminalId: string;
  restaurantId: number;
  level: PosErrorLevel;
  errorClass?: string;
  message?: string;
  stackTrace?: string;
  threadName?: string;
  loggerClass?: string;
  posAppVersion?: string;
  javaVersion?: string;
  osInfo?: string;
  posUserId?: number;
  metadata?: Record<string, any>;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: number;
}

// Inventory Types
export interface InventoryItem {
  id: number;
  restaurantId: number;
  name: string;
  sku: string;
  category: string;
  unit: string;
  quantity: number;
  minThreshold: number;
  maxThreshold: number;
  costPerUnit: number;
  lastRestocked?: string;
  warehouse?: string;
  supplier?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface StockMovement {
  id: number;
  itemId: number;
  itemName: string;
  quantity: number;
  type: 'IN' | 'OUT' | 'ADJUSTMENT' | 'RETURN';
  reason: string;
  reference?: string;
  createdBy: string;
  createdAt: string;
}

export interface InventoryAlert {
  id: number;
  itemId: number;
  itemName: string;
  type: 'LOW_STOCK' | 'OVERSTOCK' | 'EXPIRING';
  threshold: number;
  currentQuantity: number;
  createdAt: string;
}

export interface InventoryCategory {
  id: number;
  restaurantId: number;
  name: string;
  description?: string;
  itemCount: number;
  active: boolean;
}

// Report Types
export interface ReportMetrics {
  revenue: number;
  orders: number;
  averageOrderValue: number;
  topSellingItems: Array<{ name: string; quantity: number; revenue: number }>;
  revenueByCategory: Array<{ category: string; revenue: number }>;
  revenueByHour: Array<{ hour: number; revenue: number }>;
  paymentMethodBreakdown: Array<{ method: string; amount: number; count: number }>;
}

export interface InventoryReport {
  totalValue: number;
  itemCount: number;
  lowStockCount: number;
  turnoverRate: number;
  topMovingItems: Array<{ name: string; movements: number }>;
  categoryBreakdown: Array<{ category: string; value: number; count: number }>;
}

export interface ItemsReport {
  totalItemsSold: number;
  ticketLineCount: number;
  totalItemsRevenue: number;
  distinctItemsCount: number;
  averageItemPrice: number;
  topSellingItems: Array<{
    name: string;
    quantity: number;
    revenue: number;
    subitems?: Array<{ name: string; quantity: number; revenue: number }>;
  }>;
  revenueByCategory: Array<{ category: string; quantity: number; revenue: number }>;
  itemsSoldByHour: Array<{ hour: number; quantity: number; revenue: number }>;
}

export interface OperationsReport {
  totalTurns: number;
  totalCovers: number;
  avgCoversPerTurn: number;
  avgSessionMinutes: number;
  medianSessionMinutes: number;
  seatOccupancyRate: number;     // 0-1
  revPASH: number;
  spendPerCover: number;
  spendPerTurn: number;
  totalSeats: number;
  peakHours: { hour: number; turns: number; covers: number }[];
  occupancyHeatmap: { dayOfWeek: number; hour: number; occupiedTableHours: number; intensity: number }[];
  sessionLengthHistogram: { label: string; count: number }[];
  tableTurns: { tableId: number; tableName: string; floorName: string | null; turns: number; covers: number; avgSessionMinutes: number; revenue: number }[];
  floorUtilization: { floorName: string; tableCount: number; turns: number; revenue: number }[];
  serverPerformance: { serverName: string; turns: number; covers: number; revenue: number; avgSessionMinutes: number }[];
  orderTypeMix: { orderType: string; tickets: number; revenue: number; sharePct: number }[];
  bookingOps: {
    totalBookings: number;
    noShows: number;
    noShowRate: number;
    avgPartySize: number;
    bySource: { source: string; count: number }[];
  };
}

export interface ReportRequest {
  type: 'REVENUE' | 'INVENTORY' | 'ITEMS' | 'LABOR';
  startDate: string;
  endDate: string;
  restaurantId: number;
  groupBy?: 'DAY' | 'WEEK' | 'MONTH';
}

export interface RevenueTrendPoint {
  date: string;
  revenue: number;
  orders: number;
}

// ── Sales-tab reports (ticket / payment level) ──

export interface SalesDetailReport {
  totals: {
    subtotal: number; tax: number; discount: number; serviceCharge: number; deliveryCharge: number;
    tip: number; total: number; orders: number; covers: number;
  };
  tickets: Array<{
    posTicketId: number | null;
    ticketNumber: string | null;
    closedAt: string | null;
    orderType: string | null;
    serverName: string | null;
    tableName: string | null;
    guests: number | null;
    subtotal: number; tax: number; discount: number; serviceCharge: number; deliveryCharge: number;
    tip: number; total: number; status: string | null;
  }>;
}

export interface SalesSummaryReport {
  totals: {
    subtotal: number; tax: number; discount: number; serviceCharge: number; deliveryCharge: number;
    tip: number; total: number; orders: number; covers: number;
  };
  byPaymentMethod: Array<{ method: string; amount: number; tip: number; count: number }>;
  byOrderType: Array<{
    orderType: string; orders: number; subtotal: number; tax: number;
    discount: number; serviceCharge: number; deliveryCharge: number; total: number;
  }>;
  byServer: Array<{ serverName: string; orders: number; total: number; tip: number }>;
  byDay: Array<{
    date: string; orders: number; subtotal: number; tax: number; discount: number;
    serviceCharge: number; deliveryCharge: number; tip: number; total: number; cash: number; card: number; other: number;
  }>;
  byHour: Array<{ hour: number; orders: number; covers: number; revenue: number }>;
}

/** Per-tax-rate tax report mirroring the POS Tax Report. */
export interface TaxReport {
  totalTax: number;
  totalAmount: number;
  totalExclTax: number;
  totalItemCount: number;
  rows: Array<{
    taxRate: number; taxName: string; itemCount: number;
    totalAmount: number; taxAmount: number; exclTax: number;
  }>;
  byDay: Array<{
    date: string; taxRate: number; taxName: string; itemCount: number;
    totalAmount: number; taxAmount: number; exclTax: number;
  }>;
}

/** Discount report: per-ticket detail + by-discount-name summary, mirroring the POS report. */
export interface DiscountReport {
  totalDiscount: number;
  grossSales: number;
  discountedTicketCount: number;
  perTicket: Array<{
    ticketNumber: string | null; posTicketId: number; orderType: string | null;
    date: string | null; discountName: string; discountAmount: number;
  }>;
  byDiscountName: Array<{ name: string; count: number | null; amount: number }>;
  byDay: Array<{ date: string; amount: number }>;
}

export interface MenuBreakdownReport {
  totals: { quantity: number; revenue: number; distinctItems: number };
  items: Array<{ name: string; category: string; group: string; quantity: number; revenue: number }>;
  byGroup: Array<{ name: string; quantity: number; revenue: number }>;
  byCategory: Array<{ name: string; quantity: number; revenue: number }>;
  foodVsDrink: Array<{ name: string; quantity: number; revenue: number }>;
  alcoholFoodDrink: Array<{ name: string; quantity: number; revenue: number }>;
}

/** One row of the Food vs Drink report (a weekday, or the grand "Total" row). */
export interface FoodDrinkDayRow {
  day: string;
  foodCount: number;
  foodAmount: number;
  drinkCount: number;
  drinkAmount: number;
  discountCount: number;
  discountAmount: number;
  totalItems: number;
  totalValue: number;
}

/** Food vs Drink report: per-weekday matrix + grand total, matching the POS report. */
export interface FoodDrinkReport {
  days: FoodDrinkDayRow[];
  total: FoodDrinkDayRow;
}

/** One row of the Alcohol / Food / Drink report. */
export interface AlcoholFoodDrinkRow {
  name: string;
  count: number;
  amount: number;
}

/** Alcohol / Food / Drink report: 3 summary rows (Food, Soft Drink, Alcohol) + grand total. */
export interface AlcoholFoodDrinkReport {
  rows: AlcoholFoodDrinkRow[];
  total: AlcoholFoodDrinkRow;
}

/** One ticket row of the Delivery Charge report. */
export interface DeliveryChargeRow {
  ticketNumber: string | null;
  closedAt: string | null;
  orderType: string | null;
  customerName: string | null;
  deliveryCharge: number;
}

/** Delivery Charge report: per-ticket listing (charge > 0) with a grand total. */
export interface DeliveryChargeReport {
  tickets: DeliveryChargeRow[];
  ticketCount: number;
  totalDeliveryCharge: number;
}

/** One line item on an Exceptions report ticket. */
export interface ExceptionItemRow {
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

/** One ticket row of the Exceptions report, with its own line items. */
export interface ExceptionTicketRow {
  ticketNumber: string | null;
  posTicketId: number | null;
  orderType: string | null;
  serverName: string | null;
  tableName: string | null;
  closedAt: string | null;
  totalAmount: number;
  items: ExceptionItemRow[];
}

/** A voided- or refunded-tickets section: its tickets plus a count and total. */
export interface ExceptionsSection {
  tickets: ExceptionTicketRow[];
  ticketCount: number;
  totalAmount: number;
}

/** Exceptions report: voided and refunded ticket sections, each with line items and a total. */
export interface ExceptionsReport {
  voided: ExceptionsSection;
  refunded: ExceptionsSection;
}

export interface SalesAreaReport {
  totals: { quantity: number; revenue: number; distinctItems: number };
  areas: Array<{
    name: string;
    quantity: number;
    revenue: number;
    items: Array<{ posMenuItemId: number | null; name: string; price: number; quantity: number; revenue: number }>;
  }>;
}

export interface PaymentsReport {
  totals: { amount: number; tip: number; count: number; credit: number; debit: number };
  byType: Array<{ paymentType: string; amount: number; tip: number; count: number; credit: number; debit: number }>;
  transactions: Array<{
    processedAt: string | null;
    ticketNumber: string | null;
    posTicketId: number | null;
    serverName: string | null;
    paymentType: string | null;
    transactionType: string | null;
    customPaymentName: string | null;
    amount: number; tip: number; change: number;
    cardType: string | null;
    cardLastFour: string | null;
    authorizationCode: string | null;
    referenceNumber: string | null;
    status: string | null;
  }>;
  tipsByServer: Array<{ serverName: string; tip: number; orders: number }>;
  tipLines: Array<{
    ticketNumber: string | null;
    posTicketId: number | null;
    serverName: string | null;
    date: string | null;
    amount: number;
  }>;
  transactionsTruncated: boolean;
}

// Synced Entity Types (read-only entities synced from POS terminals)

export interface SyncedBookingInfo {
  id: number;
  uuid: string;
  startTime: string;          // ISO-8601 instant
  endTime: string | null;
  partySize: number | null;
  customerName: string | null;
  customerPhone: string | null;
  customerEmail: string | null;
  customerId: number | null;
  shopTableIds: string | null;       // CSV: "1,2,3"
  shopTableNames: string | null;     // CSV: "Table 1,Table 2"
  bookingSectionId: number | null;   // POS-native BookingSection id; authoritative over bookingSection
  bookingSection: string | null;     // denormalized display name fallback
  status: string | null;             // PENDING|CONFIRMED|SEATED|COMPLETED|CANCELLED|NO_SHOW
  source: string | null;
  notes: string | null;
  specialRequests: string | null;
  depositAmount: number | null;
  depositPaid: boolean | null;
  depositUuid: string | null;
  minimumSpend: number | null;
  minimumSpendMet: boolean | null;
  minimumSpendRuleName: string | null;
  minimumSpendOverrideByUserId: number | null;
  version: number | null;
  createdAt: string | null;
  modifiedAt: string | null;
  createdByUserId: number | null;
  modifiedByUserId: number | null;
  syncedAt: string | null;
}

export type SyncedBookingStatus = 'PENDING' | 'CONFIRMED' | 'SEATED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';

export interface BookingMutationPayload {
  startTime: string;                  // ISO-8601 instant
  endTime?: string | null;
  partySize?: number | null;
  customerName?: string | null;
  customerPhone?: string | null;
  customerEmail?: string | null;
  shopTableIds?: string | null;       // CSV: "1,2,3"
  shopTableNames?: string | null;
  bookingSectionId?: number | null;  // POS-native BookingSection id; authoritative over bookingSection
  bookingSection?: string | null;    // denormalized display name, derived client-side from the selection
  status?: SyncedBookingStatus | null;
  notes?: string | null;
  specialRequests?: string | null;
  depositAmount?: number | null;
  depositPaid?: boolean | null;
}

export interface SyncedTableInfo {
  id: number;
  posTableId: number;
  tableName: string | null;
  capacity: number | null;
  floorId: number | null;
  floorName: string | null;
  uuid: string | null;
}

export interface TranslationEntry {
  id: string;
  text: string;
  languageId: string;
}

export interface SyncedMenuCategory {
  id: number;
  posCategoryId?: number;
  uuid?: string;
  name: string;
  isVisible: boolean;
  isBeverage: boolean;
  isVisibleOnSite: boolean;
  isShortcut: boolean;
  isMenuGroupShortcut: boolean;
  sortOrder: number;
  buttonColorCode?: number;
  textColorCode?: number;
  lastChangeDate?: string;
  translations?: TranslationEntry[];
  shiftIds?: number[];
  syncedAt?: string;
}

export interface SyncedMenuGroup {
  id: number;
  posGroupId?: number;
  uuid?: string;
  name: string;
  description?: string;
  isVisible: boolean;
  isEnforceInstructions: boolean;
  isVisibleOnSite: boolean;
  isStaffMeal: boolean;
  isOnlyManager: boolean;
  isShowNotification: boolean;
  notification?: string;
  shortcutId?: number;
  categoryId?: number;
  categoryName?: string;
  sortOrder: number;
  buttonColorCode?: number;
  textColorCode?: number;
  lastChangeDate?: string;
  translations?: TranslationEntry[];
  shiftIds?: number[];
  syncedAt?: string;
}

export interface SyncedMenuItem {
  id: number;
  posMenuItemId?: number;
  uuid?: string;
  name: string;
  description?: string;
  price: number;
  groupId?: number;
  groupName?: string;
  visible: boolean;
  sortOrder: number;
  taxRate?: number;
  printerGroup?: string;
  imageUrl?: string;
  barcode?: string;
  translations?: TranslationEntry[];
  syncedAt?: string;
  // Extended fields
  unitName?: string;
  buyPrice?: number;
  stockAmount?: number;
  manageStock?: boolean;
  disableWhenStockAmountIsZero?: boolean;
  webPrice?: number;
  discountRate?: number;
  minQuantityGroup?: number;
  maxQuantityGroup?: number;
  enforceInstructions?: boolean;
  appendToParentName?: boolean;
  prependToParentName?: boolean;
  buttonColorCode?: number;
  textColorCode?: number;
  visibleOnSite?: boolean;
  hideOnKitchen?: boolean;
  showStatusButton?: boolean;
  hideOnReceipt?: boolean;
  askMealDeal?: boolean;
  askForPrice?: boolean;
  giveRestaurantPoints?: boolean;
  alcoholicDrink?: boolean;
  hasDouble?: boolean;
  freeDisplayForChild?: boolean;
  prependAsterisk?: boolean;
  disableCookingInfoOnline?: boolean;
  openItem?: boolean;
  saleAreaId?: string;
  taxId?: number;
  printerGroupId?: number;
  orderTypeIds?: number[];
  cookingInstructionIds?: number[];
  dietaryFlags?: Record<string, boolean>;
  additionalPrinterGroupIds?: number[];
  shiftIds?: number[];
  bannedTableIds?: number[];
  modifierGroups?: ModifierGroupData[];
}

export interface ModifierGroupData {
  uuid?: string;
  menuGroupId?: number;
  menuGroupName?: string;
  minQuantity?: number;
  maxQuantity?: number;
  sortOrder?: number;
  selectMax?: boolean;
  selectMaxWithinGroup?: boolean;
  extra?: boolean;
  minTicketValueToBeAvailable?: number;
}

export interface SyncedTax {
  id: number;
  posTaxId?: number;
  uuid?: string;
  name: string;
  rate: number;
  lastChangeDate?: string;
  syncedAt?: string;
}

export interface SyncedDiscount {
  id: number;
  posDiscountId?: number;
  uuid?: string;
  name: string;
  type?: number;
  barcode?: string;
  qualificationType?: number;
  isApplyToAll?: boolean;
  isEnabled: boolean;
  isAutoApply: boolean;
  isAutoApplyAfterSelection?: boolean;
  isNeverExpire?: boolean;
  isOnlyFood?: boolean;
  isOnlyDrinks?: boolean;
  isWebsiteOnly?: boolean;
  isApplyToDeliveryCharge?: boolean;
  isApplyOnLast?: boolean;
  minimumBuy?: number;
  value?: number;
  minQuantity?: number;
  maxValuePerGuest?: number;
  expiryDate?: string;
  orderTypeIds?: number[];
  shiftIds?: number[];
  menuItemIds?: number[];
  syncStatus?: string;
  createdDate?: string;
  modifiedDate?: string;
}

export interface SyncedOrderType {
  id: number;
  posOrderTypeId?: number;
  uuid?: string;
  name: string;
  branchName?: string;
  branchPhone?: string;
  isEnabled?: boolean;
  isPrepaid: boolean;
  isDelivery: boolean;
  isKiosk: boolean;
  isBarTab: boolean;
  sortOrder: number;
  colorCode?: number;
  textColor?: number;
  // --- Extended fields ---
  isShowTableSelection?: boolean;
  isShowGuestSelection?: boolean;
  isShouldPrintToKitchen?: boolean;
  isCloseOnPaid?: boolean;
  isRequiredCustomerData?: boolean;
  isShowItemBarcode?: boolean;
  isShowInLoginScreen?: boolean;
  isConsolidateItemsInReceipt?: boolean;
  isAllowSeatBasedOrder?: boolean;
  isHideItemWithEmptyInventory?: boolean;
  isHasForHereAndToGo?: boolean;
  isPreAuthCreditCard?: boolean;
  isSendToBar?: boolean;
  isShowPriceOnButton?: boolean;
  isShowStockCountOnButton?: boolean;
  isShowUnitPriceInTicketGrid?: boolean;
  bookingType?: string;
  addProducts?: string;
  isAutomaticallySendToKitchen?: boolean;
  timeInAdvanceToSendToKitchen?: number;
  ticketReceiptName?: string;
  kitchenReceiptName?: string;
  barReceiptName?: string;
  isManuallySendToKitchen?: boolean;
  isUseKitchenDisplayOrder?: boolean;
  isExternalDeliveryProvider?: boolean;
  isHasZReport?: boolean;
  isAllowPayTicketsWithServiceCharge?: boolean;
  isCloseKitchenTicketWhenCloseTicket?: boolean;
  isPrintTicketNumber?: boolean;
  isPrintReceiptOnPaymentDone?: boolean;
  timeToPrintOnPaymentDone?: number;
  startDailyIdFrom?: number;
  bookingRequestTimeout?: number;
  bookingRejectedTimeDefault?: number;
  bookingMessages?: string;
  isApproveWithoutConfirmation?: boolean;
  temporaryApproveTime?: number;
  autoApprovedStatuses?: string;
  isStaffMeal?: boolean;
  isPreapproved?: boolean;
  isPrintShortReceipt?: boolean;
  isPrintOnBookingComplete?: boolean;
  isPrintKitchenOnPaymentComplete?: boolean;
  isWasted?: boolean;
  isShowCookingInstructionOnReceipt?: boolean;
  isShowKitchenDisplayOrderOnTicketReceipt?: boolean;
  isPrintTicketInstedKitchenTicket?: boolean;
  isAskPaymentOnDone?: boolean;
  isShowInDispatchOrder?: boolean;
  isPrintPaymentInfo?: boolean;
  isShowUnpaidOnReceipt?: boolean;
  isShowValueOnPaymentInfo?: boolean;
  isEnforceDeliveryCharge?: boolean;
  isAutoaproveSimpleBookings?: boolean;
  isAutoaproveBookingChanges?: boolean;
  isPlayAutoaproveSound?: boolean;
  isShowTipSugestion?: boolean;
  tipSugestionPercentages?: string;
  footerMessage?: string;
  minuteAsap?: number;
  isNotPrintOnlineOnSettle?: boolean;
  labelTimeRequested?: string;
  isSortItemsByGroupInReceipt?: boolean;
  isNoPrintToBar?: boolean;
  customerQRCode?: string;
  syncStatus?: string;
  createdDate?: string;
  modifiedDate?: string;
}

export interface SyncedCustomPayment {
  id: number;
  posCustomPaymentId?: number;
  uuid?: string;
  name: string;
  isRequiredRefNumber: boolean;
  isUseForPayByVoucher: boolean;
  isUniqueReferenceNumber: boolean;
  isUseFrontButton: boolean;
  isOpenDrawerOnPayment: boolean;
  isVoucher: boolean;
  refNumberFieldName?: string;
  cardType?: string;
  sortOrder: number;
  buttonColorCode?: number;
  textColorCode?: number;
  syncedAt?: string;
}

export interface SyncedCookingInstruction {
  id: number;
  posInstructionId?: number;
  uuid?: string;
  description: string;
  isGeneral: boolean;
  isFoodOnly: boolean;
  isDrinkOnly: boolean;
  buttonColorCode?: number;
  textColorCode?: number;
  sortOrder: number;
  lastChangeDate?: string;
  syncedAt?: string;
}

export interface SyncedKitchenDisplayOrder {
  id: number;
  posKitchenDisplayOrderId?: number;
  uuid?: string;
  name: string;
  isEnabled: boolean;
  isDefaultDisplay: boolean;
  isWhenReady: boolean;
  isAllTogether: boolean;
  orderNo: number;
  colorOrder?: number;
  buttonColorCode?: number;
  textColorCode?: number;
  menuGroupIds?: string;
  lastChangeDate?: string;
  syncedAt?: string;
}

export interface SyncedPrinterGroup {
  id: number;
  posPrinterGroupId?: number;
  uuid?: string;
  name: string;
  isDefault: boolean;
  type?: string;
  printerNames?: string;
  syncStatus?: string;
  createdDate?: string;
  modifiedDate?: string;
}

export interface SyncedAccountSalesArea {
  id: number;
  posAccountSalesAreaId?: string;
  uuid?: string;
  name: string;
  isEnabled: boolean;
  sortOrder: number;
  menuGroupIds?: string;
  lastChangeDate?: string;
  syncedAt?: string;
}

export interface SyncedAskNextReason {
  id: number;
  posAskNextReasonId?: string;
  uuid?: string;
  name: string;
  isEnabled: boolean;
  sortOrder: number;
  colorOrder?: number;
  buttonColorCode?: number;
  textColorCode?: number;
  type?: string;
  printerGroupId?: number;
  printerGroupName?: string;
  syncStatus?: string;
  createdDate?: string;
  modifiedDate?: string;
}

export interface SyncedLanguage {
  id: number;
  posLanguageId?: string;
  uuid?: string;
  name: string;
  isEnabled: boolean;
  syncedAt?: string;
}

export interface SyncedShift {
  id: number;
  posShiftId?: number;
  uuid?: string;
  name: string;
  startTime: string;
  endTime: string;
  shiftLength?: number;
  daysOfWeek?: string;
  startDate?: string;
  endDate?: string;
  syncStatus?: string;
  createdDate?: string;
  modifiedDate?: string;
}

export type SyncedEntity =
  | SyncedMenuCategory
  | SyncedMenuGroup
  | SyncedMenuItem
  | SyncedTax
  | SyncedDiscount
  | SyncedOrderType
  | SyncedCustomPayment
  | SyncedCookingInstruction
  | SyncedKitchenDisplayOrder
  | SyncedPrinterGroup
  | SyncedAccountSalesArea
  | SyncedAskNextReason
  | SyncedLanguage
  | SyncedShift;

export type EntityType =
  | 'menuCategories'
  | 'menuGroups'
  | 'menuItems'
  | 'taxes'
  | 'discounts'
  | 'orderTypes'
  | 'customPayments'
  | 'cookingInstructions'
  | 'kitchenDisplayOrders'
  | 'printerGroups'
  | 'accountSalesAreas'
  | 'askNextReasons'
  | 'languages'
  | 'shifts';

// ==================== Draft Mode ====================

/**
 * A dashboard area that can independently stage its changes instead of
 * sending them straight to the POS. MENU covers the Menu page, FLOOR the
 * Floors & Zones tab and the layout editor.
 */
export type SyncDraftArea = 'MENU' | 'FLOOR';

/** Draft mode state for one area of one restaurant. */
export interface SyncDraftStatus {
  area: SyncDraftArea;
  /** Whether changes in this area are being staged rather than sent. */
  enabled: boolean;
  /** How many changes are staged and waiting to be published. */
  stagedChangeCount: number;
  /** Username of whoever turned draft mode on; null when it is off. */
  enabledBy: string | null;
  enabledAt: string | null;
  /** UTC ISO instant the staged changes will be pushed automatically; null if not scheduled. */
  scheduledPushAt: string | null;
  /** Username of whoever scheduled the push; null when nothing is scheduled. */
  scheduledBy: string | null;
}
